import { Fragment, useEffect, useRef, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { QuestionMarkCircleIcon } from '@heroicons/react/24/outline';
import Spinner from '../../../component/common/Spinner';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { errorToast } from '../../../utils/helper';
import { B2BWellPointsPageData } from '../../../utils/pageContent';

const validationSchema = Yup.object({
    file: Yup.string().required(B2BWellPointsPageData.approvePaymentPopUp.documentRequriedMessage),
});

const ApprovePaymentPopup = ({
    open,
    onClose,
    onSubmit,
    isLoadingUpdate,
    message = B2BWellPointsPageData.approvePaymentPopUp.paymentApprovalmessage
}) => {
    const cancelButtonRef = useRef(null);

    const initialValues = {
        file: null
    };

    const formik = useFormik({
        initialValues,
        validationSchema,
        onSubmit: async (values) => {
            await onSubmit(values);
        }
    });

    const handleClose = () => {
        formik.resetForm();
        onClose();
    };

    useEffect(() => {
        if (!open) {
            formik.resetForm();
        }
    }, [open]);


    const handleUploadDocumentImage = (e) => {
        const file = e.target.files[0];
        if (!file?.name?.match(/\.(jpg|jpeg|png)$/i)) {
            errorToast(B2BWellPointsPageData.approvePaymentPopUp.imageFormatNotSupportedMessage);
            formik.setFieldError("file", B2BWellPointsPageData.approvePaymentPopUp.allowedFormatMessage);
        } else {
            formik.setFieldValue("file", file);
        }
    };

    const getFilePreviewUrl = (file) => {
        return file instanceof File ? URL.createObjectURL(file) : file;
    };

    return (
        <Transition.Root appear show={open} as={Fragment}>
            <Dialog
                as="div"
                className="relative z-50"
                initialFocus={cancelButtonRef}
                onClose={() => {
                    handleClose();
                }}
            >
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                </Transition.Child>
                <div className="fixed inset-0 z-50 overflow-y-auto">
                    <div className="flex min-h-full  justify-center p-4 text-center items-center sm:p-0">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 scale-95"
                            enterTo="opacity-100 scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 scale-100"
                            leaveTo="opacity-0 scale-95"
                        >
                            <Dialog.Panel className="relative transform overflow-hidden rounded-3xl bg-white dark:bg-shoorah-darkBgTabColor dark:border-shoorah-darkBorder dark:text-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
                                <form onSubmit={formik.handleSubmit}>
                                    <div className="bg-white dark:bg-shoorah-darkBgTabColor dark:border-shoorah-darkBorder dark:text-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                                        <div className="sm:flex sm:items-start">
                                            <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-blue-100 sm:mx-0 sm:h-10 sm:w-10">
                                                <QuestionMarkCircleIcon
                                                    className="h-6 w-6 text-shoorah-secondary"
                                                    aria-hidden="true"
                                                />
                                            </div>
                                            <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                                                <Dialog.Title
                                                    as="h3"
                                                    className="text-lg font-medium leading-6 dark:text-white text-gray-900"
                                                >
                                                    {B2BWellPointsPageData.approvePaymentPopUp.confirmBtnText}
                                                </Dialog.Title>
                                                <p className="my-2 text-sm dark:text-white text-gray-500">{message}</p>
                                                <div className="mt-4">
                                                    <div>
                                                        <label htmlFor="document" className="block text-sm font-medium dark:text-white text-gray-700 mb-1">
                                                            {B2BWellPointsPageData.approvePaymentPopUp.uploadDocumentLabel}
                                                        </label>
                                                        <div className={`relative inline-flex`}>
                                                            {formik.values.file ? (
                                                                <div className="relative group">
                                                                    <img
                                                                        src={getFilePreviewUrl(formik.values.file)}
                                                                        alt="document"
                                                                        className="w-[200px] h-[200px] object-cover rounded-lg cursor-pointer "
                                                                        style={{ aspectRatio: '1 / 1', overflow: 'hidden' }}
                                                                    />
                                                                    {!isLoadingUpdate &&
                                                                        <label
                                                                            htmlFor="document"
                                                                            className="absolute cursor-pointer top-0 left-auto right-0 w-10 h-10 rounded-lg bg-black opacity-0 group-hover:opacity-50 flex items-center justify-center duration-100 transition-all ease-in-out"
                                                                        >
                                                                            <svg
                                                                                xmlns="http://www.w3.org/2000/svg"
                                                                                viewBox="0 0 24 24"
                                                                                width="18"
                                                                                height="18"
                                                                                fill="none"
                                                                                stroke="#fff"
                                                                                strokeWidth="2"
                                                                                strokeLinecap="round"
                                                                                strokeLinejoin="round"
                                                                            >
                                                                                <path d="M12 20h9" />
                                                                                <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19.001 3 20l.999-4 12.5-12.5z" />
                                                                            </svg>
                                                                        </label>
                                                                    }
                                                                </div>
                                                            ) : (
                                                                <>
                                                                    <label
                                                                        htmlFor="document"
                                                                        className="cursor-pointer mb-5 sm:mb-0 flex items-center justify-center bg-shoorah-normanGray w-[200px] h-[200px] rounded-lg dark:bg-shoorah-darkBgColor"
                                                                        style={{ aspectRatio: '1 / 1', overflow: 'hidden' }}
                                                                    >
                                                                        <svg
                                                                            width="40"
                                                                            height="40"
                                                                            viewBox="0 0 40 40"
                                                                            fill="none"
                                                                            xmlns="http://www.w3.org/2000/svg"
                                                                            className="dark:fill-shoorah-darkBg"
                                                                        >
                                                                            <rect
                                                                                x="0.75"
                                                                                y="0.75"
                                                                                width="38.5"
                                                                                height="38.5"
                                                                                rx="7.25"
                                                                                fill="#F4F4F4"
                                                                                stroke="#E7E8EA"
                                                                                strokeWidth="1.5"
                                                                                className="fill-shoorah-normanGray dark:fill-shoorah-darkBgColor stroke-shoorah-borderGray dark:stroke-shoorah-darkBgColor"
                                                                            />
                                                                            <path
                                                                                d="M17 30H23C28 30 30 28 30 23V17C30 12 28 10 23 10H17C12 10 10 12 10 17V23C10 28 12 30 17 30Z"
                                                                                stroke="#0B1D2E"
                                                                                strokeWidth="1.5"
                                                                                strokeLinecap="round"
                                                                                strokeLinejoin="round"
                                                                            />
                                                                            <path
                                                                                d="M17 18C18.1046 18 19 17.1046 19 16C19 14.8954 18.1046 14 17 14C15.8954 14 15 14.8954 15 16C15 17.1046 15.8954 18 17 18Z"
                                                                                stroke="#0B1D2E"
                                                                                strokeWidth="1.5"
                                                                                strokeLinecap="round"
                                                                                strokeLinejoin="round"
                                                                            />
                                                                            <path
                                                                                d="M10.6699 26.9496L15.5999 23.6396C16.3899 23.1096 17.5299 23.1696 18.2399 23.7796L18.5699 24.0696C19.3499 24.7396 20.6099 24.7396 21.3899 24.0696L25.5499 20.4996C26.3299 19.8296 27.5899 19.8296 28.3699 20.4996L29.9999 21.8996"
                                                                                stroke="#0B1D2E"
                                                                                strokeWidth="1.5"
                                                                                strokeLinecap="round"
                                                                                strokeLinejoin="round"
                                                                            />
                                                                        </svg>
                                                                    </label>
                                                                </>
                                                            )}
                                                            <input
                                                                type="file"
                                                                id="document"
                                                                hidden
                                                                accept=".jpg, .jpeg, .png"
                                                                onChange={handleUploadDocumentImage}
                                                            // disabled={!!tempImagePreview}
                                                            />
                                                        </div>
                                                        {formik.touched.file && formik.errors.file && (
                                                            <p className="text-red-600 text-sm mt-1">
                                                                {formik.errors.file}
                                                            </p>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="bg-gray-50 dark:bg-shoorah-darkBgTabColor px-4 py-4 sm:flex sm:flex-row-reverse sm:px-6">
                                        <button
                                            type="submit"
                                            className="btn-primary w-full sm:ml-3 sm:w-auto"
                                            disabled={isLoadingUpdate}
                                        >
                                            {isLoadingUpdate ? <Spinner /> : B2BWellPointsPageData.approvePaymentPopUp.markAsPaidBtnText}
                                        </button>
                                        <button
                                            type="button"
                                            className="mt-3 inline-flex w-full hover:bg-inherit justify-center rounded-3xl border border-gray-300 bg-white dark:bg-shoorah-darkBgTabColor dark:border-shoorah-darkBorder dark:text-white px-6 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                                            onClick={() => {
                                                handleClose();
                                            }}
                                            ref={cancelButtonRef}
                                        >
                                            {B2BWellPointsPageData.approvePaymentPopUp.cancelBtnText}
                                        </button>
                                    </div>
                                </form>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition.Root>
    );
};
export default ApprovePaymentPopup;
